import {
  Avatar,
  Box,
  CardActionArea,
  Drawer,
  Link,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { Fragment, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { MHidden } from '../../components/@material-extend';
import NavSection from '../../components/NavSection';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';

import { keycloakService } from '../../../keycloak';

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.complex
    })
  }
}));

const ScrollableContent = styled('div')({
  maxHeight: '1123px',
  overflowY: 'scroll',
  scrollbarWidth: 'none', // For Firefox
  msOverflowStyle: 'none', // For Internet Explorer and Edge
  '::-webkit-scrollbar': {
    display: 'none' // For Chrome, Safari, and Opera
  }
});

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[500_12]
}));

type IconCollapseProps = {
  onToggleCollapse: VoidFunction;
  collapseClick: boolean;
};

function IconCollapse({ onToggleCollapse, collapseClick }: IconCollapseProps) {
  return (
    <Tooltip title="Mini Menu">
      <CardActionArea
        onClick={onToggleCollapse}
        sx={{
          width: 18,
          height: 18,
          display: 'flex',
          cursor: 'pointer',
          borderRadius: '50%',
          alignItems: 'center',
          color: 'text.primary',
          justifyContent: 'center',
          border: 'solid 1px currentColor',
          ...(collapseClick && {
            borderWidth: 2
          })
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'currentColor',
            transition: (theme) => theme.transitions.create('all'),
            ...(collapseClick && {
              width: 0,
              height: 0
            })
          }}
        />
      </CardActionArea>
    </Tooltip>
  );
}

type DashboardSidebarProps = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
  sidebarConfig: any;
  roles?: string[];
};

export default function DashboardSidebar({
  isOpenSidebar,
  onCloseSidebar,
  sidebarConfig,
  roles
}: DashboardSidebarProps) {
  const { pathname } = useLocation();

  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
  }, [pathname]);

  const renderContent = (
    <ScrollableContent>
      <Stack
        spacing={3}
        sx={{
          px: 2.5,
          pt: 3,
          pb: 2,
          ...(isCollapse && {
            alignItems: 'center'
          })
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box component={RouterLink} to={process.env.PUBLIC_URL} sx={{ display: 'inline-flex' }}>
            {useTheme().palette.mode !== 'dark' ? (
              <img
                src={`${process.env.PUBLIC_URL}/static/logo-lar-sidebar.png`}
                alt="logo"
                width="85%"
              />
            ) : (
              <img
                src={`${process.env.PUBLIC_URL}/static/logo-lar-negativa-sidebar.png`}
                alt="logo"
                width="85%"
              />
            )}
          </Box>

          <MHidden width="lgDown">
            {!isCollapse && (
              <IconCollapse onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
            )}
          </MHidden>
        </Stack>

        {isCollapse ? (
          <Avatar
            alt="My Avatar"
            src={`${process.env.PUBLIC_URL}/static/mock-images/avatars/avatar_default.jpg`}
            sx={{ mx: 'auto', mb: 2 }}
          />
        ) : (
          <Link underline="none" component={RouterLink} to="#">
            <AccountStyle>
              <Avatar
                alt="My Avatar"
                src={`${process.env.PUBLIC_URL}/static/mock-images/avatars/avatar_default.jpg`}
              />
              <Box sx={{ ml: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                  {keycloakService.getUsername()}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {roles &&
                    roles.map((role, index) => (
                      <Fragment key={index}>
                        {role}
                        <br />
                      </Fragment>
                    ))}
                </Typography>
              </Box>
            </AccountStyle>
          </Link>
        )}
      </Stack>
      <NavSection navConfig={sidebarConfig} isShow={!isCollapse} roles={roles} />
    </ScrollableContent>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH
        },
        ...(collapseClick && {
          position: 'absolute'
        })
      }}
    >
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default',
              ...(isCollapse && {
                width: COLLAPSE_WIDTH
              }),
              ...(collapseHover && {
                borderRight: 0,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
                boxShadow: (theme) => theme.customShadows.z20,
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.88)
              })
            }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
